import { CLIENT_ID } from './constants';
import type { Address } from './inputForm';

// Subset of the Pelias autocomplete response we read.
// Docs: https://github.com/pelias/documentation/blob/master/autocomplete.md
interface PeliasFeature {
    properties: {
        name: string;
        localadmin: string;
        postalcode: string;
    };
    geometry: {
        coordinates: [number, number];
    };
}

interface PeliasResponse {
    features: PeliasFeature[];
}

export async function addressSearch(text: string): Promise<Address[]> {
    const peliasApi = 'https://www.tori.fi/pelias-api/v1/autocomplete';
    const apiUrl = new URL(peliasApi);

    apiUrl.searchParams.set('text', `${text.trimEnd()}*`);
    apiUrl.searchParams.set('boundary.country', 'no');
    apiUrl.searchParams.set('lang', 'no');
    apiUrl.searchParams.set('layers', 'address');
    apiUrl.searchParams.set('sources', 'osm');
    apiUrl.searchParams.set('size', '200');

    const response = await fetch(apiUrl, {
        headers: {
            Origin: 'https://www.finn.no',
            'X-Client-Id': CLIENT_ID,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error('Failed to fetch possible addresses');
    }

    const data = (await response.json()) as PeliasResponse;

    return groupSortAddresses(mapResponseToAddresses(data));
}

const mapResponseToAddresses = (data: PeliasResponse): Address[] =>
    data.features.map((feature) => ({
        name: feature.properties.name,
        label: `${feature.properties.name}, ${feature.properties.localadmin}`,
        municipalityName: feature.properties.localadmin,
        postalCode: feature.properties.postalcode,
        lat: feature.geometry.coordinates[1],
        lon: feature.geometry.coordinates[0],
    }));

const getStreetAndNumber = (name: string) => {
    const match = name.match(/^(.*?)(\d+)([A-Za-z]?)$/);
    return match
        ? { street: match[1]!.trim(), number: Number.parseInt(match[2]!, 10), letter: match[3] ?? '' }
        : { street: name, number: 0, letter: '' };
};

function sortByStreetAndNumber(a: Address, b: Address): number {
    const aParts = getStreetAndNumber(a.name);
    const bParts = getStreetAndNumber(b.name);

    const streetDiff = aParts.street.localeCompare(bParts.street);
    if (streetDiff !== 0) return streetDiff;

    const numberDiff = aParts.number - bParts.number;
    if (numberDiff !== 0) return numberDiff;

    return aParts.letter.localeCompare(bParts.letter);
}

function groupSortAddresses(addresses: Address[]): Address[] {
    const groups: Record<string, Address[]> = {};

    addresses.forEach((address) => {
        const group = groups[address.municipalityName] ?? [];
        group.push(address);
        groups[address.municipalityName] = group;
    });

    return Object.values(groups)
        .flatMap((group) => group.sort(sortByStreetAndNumber))
        .slice(0, 10);
}
